<script>
/**
 * 현물시세 차트 페이지 (HQ 전용).
 *
 * metal_price_history 테이블의 금(GOLD) / 은(SILVER) 시세 이력을
 * 각각 독립된 Apache ECharts Smooth Spline 차트로 시각화한다.
 *
 * 리팩터링 포인트:
 * - buildChartOption(): 금/은 공통 옵션 팩토리 → 중복 제거
 * - getOrInitInstance(): v-if DOM 재생성 대응 + lazy init 통합
 * - 통계(최고가·최저가·평균가)를 각 차트 카드 헤더에 인라인 표시
 *
 * 권한: HQ(본사) 관리자만 접근 가능
 */
import * as echarts from 'echarts'
import { getMetalPriceChartData } from '@/api/metalPrice.js'

export default {
  name: 'MetalPricePage',

  data() {
    const toDateStr = (d) => d.toISOString().substring(0, 10)
    const today = new Date()
    const defaultStart = new Date(today)
    defaultStart.setDate(defaultStart.getDate() - 14)

    return {
      /* ── 검색 조건 ── */
      search: {
        startDate: toDateStr(defaultStart),
        endDate: toDateStr(today),
      },

      /* ── 차트 데이터 ── */
      goldData: [],   // [{ quotedAt, priceKrwPerG }]
      silverData: [], // [{ quotedAt, priceKrwPerG }]

      /* ── UI 상태 ── */
      loading: false,
      errorMessage: '',

      /**
       * ECharts 인스턴스.
       * v-if 토글로 DOM 재생성 시 getOrInitInstance()에서 재초기화한다.
       */
      goldChartInstance: null,
      silverChartInstance: null,
    }
  },

  computed: {
    /** 금 통계: 최고가·최저가·평균가 */
    goldStats() { return this.calcStats(this.goldData) },
    /** 은 통계: 최고가·최저가·평균가 */
    silverStats() { return this.calcStats(this.silverData) },
    /** 금 데이터 존재 여부 */
    hasGold() { return this.goldData.length > 0 },
    /** 은 데이터 존재 여부 */
    hasSilver() { return this.silverData.length > 0 },
  },

  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.fetchChartData()
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.disposeAll()
  },

  methods: {
    /* ================================================================
     * 데이터 조회
     * ================================================================ */

    /**
     * 현물시세 차트 데이터를 서버에서 조회한다.
     *
     * [중요] loading=false → nextTick(DOM 반영) → updateCharts() 순으로 호출한다.
     * v-if 조건으로 차트 컨테이너 div가 DOM에 나타난 뒤 ECharts를 초기화해야 한다.
     */
    async fetchChartData() {
      this.loading = true
      this.errorMessage = ''
      try {
        const res = await getMetalPriceChartData({
          startDate: this.search.startDate || undefined,
          endDate:   this.search.endDate   || undefined,
        })
        if (res.data?.code === 0) {
          this.goldData   = res.data.data?.gold   || []
          this.silverData = res.data.data?.silver || []
          this.loading = false
          await this.$nextTick()
          this.updateCharts()
        } else {
          this.loading = false
          this.errorMessage = res.data?.message || '데이터 조회에 실패했습니다.'
        }
      } catch {
        this.loading = false
        this.errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.'
      }
    },

    handleSearch() { this.fetchChartData() },

    handleReset() {
      const toDateStr = (d) => d.toISOString().substring(0, 10)
      const today = new Date()
      const start = new Date(today)
      start.setDate(start.getDate() - 14)
      this.search.startDate = toDateStr(start)
      this.search.endDate   = toDateStr(today)
      this.fetchChartData()
    },

    /* ================================================================
     * ECharts 인스턴스 관리 (공통)
     * ================================================================ */

    /**
     * 기존 인스턴스가 이미 DOM에서 분리(detach)됐으면 dispose 후 재초기화한다.
     * v-if/v-else 토글로 DOM이 재생성될 때마다 호출된다.
     *
     * @param {echarts.ECharts|null} instance - 현재 보유 중인 ECharts 인스턴스
     * @param {string} refName - this.$refs 키 (차트 컨테이너 ref 이름)
     * @returns {echarts.ECharts|null} 유효한 ECharts 인스턴스
     */
    getOrInitInstance(instance, refName) {
      /* DOM에서 분리된 구 인스턴스면 폐기 */
      if (instance && !document.contains(instance.getDom())) {
        instance.dispose()
        instance = null
      }
      /* 인스턴스 없으면 새로 초기화 */
      if (!instance) {
        const dom = this.$refs[refName]
        if (!dom) return null
        instance = echarts.init(dom, null, { renderer: 'canvas' })
      }
      return instance
    },

    /** 리사이즈 시 두 차트 모두 크기 갱신 */
    handleResize() {
      if (this.goldChartInstance)   this.goldChartInstance.resize()
      if (this.silverChartInstance) this.silverChartInstance.resize()
    },

    /** 컴포넌트 언마운트 시 두 인스턴스 모두 메모리 해제 */
    disposeAll() {
      if (this.goldChartInstance)   { this.goldChartInstance.dispose();   this.goldChartInstance = null }
      if (this.silverChartInstance) { this.silverChartInstance.dispose(); this.silverChartInstance = null }
    },

    /* ================================================================
     * 차트 렌더링
     * ================================================================ */

    /** 금·은 차트를 각각 갱신한다 */
    updateCharts() {
      this.goldChartInstance = this.getOrInitInstance(this.goldChartInstance, 'goldChartContainer')
      if (this.goldChartInstance) {
        this.goldChartInstance.setOption(
          this.buildChartOption(this.goldData, '금 (GOLD)', '#D4AF37', 'rgba(212,175,55,0.25)', 'rgba(212,175,55,0.02)'),
          true,
        )
      }

      this.silverChartInstance = this.getOrInitInstance(this.silverChartInstance, 'silverChartContainer')
      if (this.silverChartInstance) {
        this.silverChartInstance.setOption(
          this.buildChartOption(this.silverData, '은 (SILVER)', '#A8A9AD', 'rgba(168,169,173,0.20)', 'rgba(168,169,173,0.02)'),
          true,
        )
      }
    },

    /**
     * ECharts 옵션을 생성하는 팩토리 메서드.
     * 금/은 두 차트가 이 메서드를 공유하여 중복 코드를 제거한다.
     *
     * @param {Array<{quotedAt:string, priceKrwPerG:number}>} dataArr - 시세 데이터
     * @param {string} seriesName - 범례/tooltip에 표시할 시리즈명
     * @param {string} lineColor  - 라인 및 심볼 색상 (hex)
     * @param {string} gradientTop    - 영역 그라데이션 상단 색 (rgba)
     * @param {string} gradientBottom - 영역 그라데이션 하단 색 (rgba)
     * @returns {Object} ECharts option 객체
     */
    buildChartOption(dataArr, seriesName, lineColor, gradientTop, gradientBottom) {
      const labels = dataArr.map((d) => d.quotedAt)
      const values = dataArr.map((d) => d.priceKrwPerG)

      /**
       * Y축 동적 범위 계산.
       *
       * 기본값(0 ~ max)을 쓰면 가격 변동폭이 작을 때 그래프가 바닥에 붙어 평평하게 보인다.
       * 실제 데이터의 min/max 기준으로 상하에 버퍼를 추가해 변동폭을 극대화한다.
       *
       * - buffer = 실제 변동폭(range)의 30%
       * - yMin   = min - buffer  (하단 여유)
       * - yMax   = max + buffer  (상단 여유)
       *
       * 변동폭이 거의 없는 경우(range ≈ 0)에는 값의 1%를 최소 버퍼로 보장한다.
       */
      const minVal = Math.min(...values)
      const maxVal = Math.max(...values)
      const range  = maxVal - minVal
      const buffer = Math.max(range * 0.3, minVal * 0.005) // 변동폭 30% or 최솟값의 0.5% 중 큰 값
      const yMin   = Math.floor(minVal - buffer)
      const yMax   = Math.ceil(maxVal  + buffer)

      return {
        backgroundColor: 'transparent',
        grid: { top: 16, right: 20, bottom: 64, left: 20, containLabel: true },

        xAxis: {
          type: 'category',
          data: labels,
          boundaryGap: false,
          axisLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.45)' } },
          axisTick: { show: false },
          axisLabel: {
            color: '#a9b8cf',
            fontSize: 11,
            rotate: 30,
            margin: 10,
            /* 데이터가 많으면 일부 라벨만 표시하여 겹침 방지 */
            interval: Math.max(0, Math.floor(labels.length / 8) - 1),
          },
          splitLine: { show: false },
        },

        yAxis: {
          type: 'value',
          name: '원/g',
          /* 데이터 범위 기반 동적 min/max 적용 */
          min: yMin,
          max: yMax,
          nameTextStyle: { color: '#9fb0c9', fontSize: 11, align: 'right' },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#a9b8cf',
            fontSize: 10,
            formatter: (val) => val.toLocaleString('ko-KR'),
          },
          splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.24)', type: 'dashed' } },
        },

        tooltip: {
          trigger: 'axis',
          axisPointer: { lineStyle: { color: 'rgba(203, 213, 225, 0.85)', type: 'dashed' } },
          backgroundColor: 'rgba(15, 23, 42, 0.96)',
          borderColor: 'rgba(148, 163, 184, 0.5)',
          borderWidth: 1,
          padding: [10, 14],
          textStyle: { color: '#e5ecf8', fontSize: 12 },
          formatter(params) {
            const p = params[0]
            if (!p || p.value == null) return ''
            const price = Number(p.value).toLocaleString('ko-KR')
            return `<div style="font-weight:600;color:#f8fafc;margin-bottom:4px;">${p.axisValue}</div>
                    <div style="display:flex;align-items:center;gap:6px;">
                      <span style="width:8px;height:8px;border-radius:50%;background:${p.color};display:inline-block;"></span>
                      <span style="color:#b8c7dc;">${p.seriesName}</span>
                      <span style="margin-left:auto;padding-left:16px;font-weight:600;color:#f8fafc;">${price} 원/g</span>
                    </div>`
          },
        },

        series: [{
          name: seriesName,
          type: 'line',
          smooth: true,
          data: values,
          lineStyle: { color: lineColor, width: 2.5 },
          itemStyle: { color: lineColor, borderColor: '#fff', borderWidth: 2 },
          symbol: 'circle',
          symbolSize: 6,
          showSymbol: dataArr.length <= 30,
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: gradientTop },
              { offset: 1, color: gradientBottom },
            ]),
          },
        }],
      }
    },

    /* ================================================================
     * 통계 계산 (공통)
     * ================================================================ */

    /**
     * 시세 배열에서 최고가·최저가·평균가를 계산한다.
     * @param {Array} dataArr
     * @returns {{ max, min, avg } | null}
     */
    calcStats(dataArr) {
      if (!dataArr?.length) return null
      const prices = dataArr.map((d) => d.priceKrwPerG)
      return {
        max: Math.max(...prices),
        min: Math.min(...prices),
        avg: Math.round(prices.reduce((s, p) => s + p, 0) / prices.length),
      }
    },

    /**
     * 숫자를 천 단위 콤마 포맷으로 변환한다.
     * @param {number} val
     * @returns {string}
     */
    formatPrice(val) {
      return val != null ? Number(val).toLocaleString('ko-KR') : '-'
    },
  },
}
</script>

<template>
  <div class="metal-price-page">
    <!-- 페이지 타이틀 -->
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">현물시세</div>
        <div class="text-body-2 text-grey mt-1">금 / 은 시세 추이</div>
      </div>
    </div>

    <!-- 검색 필터 -->
    <v-card rounded="lg" elevation="0" border class="mb-5">
      <v-card-text class="pa-4">
        <v-row align="center" no-gutters>
          <v-col cols="12" sm="auto" class="mr-3 mb-2 mb-sm-0">
            <v-text-field
              v-model="search.startDate"
              type="date"
              label="시작일"
              density="compact"
              variant="outlined"
              hide-details
              style="min-width:160px;"
            />
          </v-col>
          <v-col cols="auto" class="mr-3 mb-2 mb-sm-0 text-grey-darken-1">~</v-col>
          <v-col cols="12" sm="auto" class="mr-4 mb-2 mb-sm-0">
            <v-text-field
              v-model="search.endDate"
              type="date"
              label="종료일"
              density="compact"
              variant="outlined"
              hide-details
              style="min-width:160px;"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" variant="flat" :loading="loading" class="mr-2" @click="handleSearch">
              <v-icon start>mdi-magnify</v-icon>조회
            </v-btn>
            <v-btn color="grey-lighten-1" variant="flat" :disabled="loading" @click="handleReset">
              <v-icon start>mdi-refresh</v-icon>초기화
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 에러 -->
    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      rounded="lg"
      class="mb-4"
      closable
      @click:close="errorMessage = ''"
    >
      {{ errorMessage }}
    </v-alert>

    <!-- 로딩 -->
    <div v-if="loading" class="d-flex justify-center align-center py-16">
      <v-progress-circular indeterminate color="amber-darken-2" size="48" />
    </div>

    <template v-else>

      <!-- ────────────────────── 금(GOLD) 차트 카드 ────────────────────── -->
      <v-card rounded="lg" elevation="0" border class="mb-4">
        <!-- 헤더: 시리즈명 + 통계 인라인 -->
        <v-card-title class="pa-4 pb-3">
          <div class="d-flex align-center flex-wrap gap-3">
            <!-- 시리즈 레이블 -->
            <div class="d-flex align-center">
              <span
                style="width:12px;height:12px;border-radius:50%;background:#D4AF37;display:inline-block;"
                class="mr-2"
              />
              <span class="text-subtitle-2 font-weight-bold" style="color:#f5cf7a;">금 시세</span>
            </div>

            <v-spacer />

            <!-- 통계: 최고가 / 최저가 / 평균가 -->
            <template v-if="goldStats">
              <div class="d-flex align-center gap-4">
                <div class="text-center ml-2 mr-2">
                  <div class="text-caption text-grey" style="font-size:10px;">최고가</div>
                  <div class="text-body-2 font-weight-bold" style="color:#C62828;">
                    {{ formatPrice(goldStats.max) }}<span class="text-caption text-grey ml-1">원/g</span>
                  </div>
                </div>
                <v-divider vertical />
                <div class="text-center ml-2 mr-2">
                  <div class="text-caption text-grey" style="font-size:10px;">최저가</div>
                  <div class="text-body-2 font-weight-bold" style="color:#1565C0;">
                    {{ formatPrice(goldStats.min) }}<span class="text-caption text-grey ml-1">원/g</span>
                  </div>
                </div>
                <v-divider vertical />
                <div class="text-center ml-2 mr-2">
                  <div class="text-caption text-grey" style="font-size:10px;">평균가</div>
                  <div class="text-body-2 font-weight-bold" style="color:#e5e7eb;">
                    {{ formatPrice(goldStats.avg) }}<span class="text-caption text-grey ml-1">원/g</span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4">
          <!-- 데이터 없음 -->
          <div
            v-if="!hasGold"
            class="d-flex flex-column justify-center align-center text-grey py-10"
          >
            <v-icon size="48" color="grey-lighten-2" class="mb-2">mdi-chart-line-variant</v-icon>
            <p class="text-body-2">조회된 금 시세 데이터가 없습니다.</p>
          </div>

          <!-- 금 차트 컨테이너 -->
          <div
            v-else
            ref="goldChartContainer"
            style="width:100%; height:300px;"
          />
        </v-card-text>
      </v-card>

      <!-- ────────────────────── 은(SILVER) 차트 카드 ────────────────────── -->
      <v-card rounded="lg" elevation="0" border>
        <v-card-title class="pa-4 pb-3">
          <div class="d-flex align-center flex-wrap gap-3">
            <!-- 시리즈 레이블 -->
            <div class="d-flex align-center">
              <span
                style="width:12px;height:12px;border-radius:50%;background:#A8A9AD;display:inline-block;"
                class="mr-2"
              />
              <span class="text-subtitle-2 font-weight-bold" style="color:#c9ced8;">은 시세</span>
            </div>

            <v-spacer />

            <!-- 통계 -->
            <template v-if="silverStats">
              <div class="d-flex align-center gap-4">
                <div class="text-center ml-2 mr-2">
                  <div class="text-caption text-grey" style="font-size:10px;">최고가</div>
                  <div class="text-body-2 font-weight-bold" style="color:#C62828;">
                    {{ formatPrice(silverStats.max) }}<span class="text-caption text-grey ml-1">원/g</span>
                  </div>
                </div>
                <v-divider vertical />
                <div class="text-center ml-2 mr-2">
                  <div class="text-caption text-grey" style="font-size:10px;">최저가</div>
                  <div class="text-body-2 font-weight-bold" style="color:#1565C0;">
                    {{ formatPrice(silverStats.min) }}<span class="text-caption text-grey ml-1">원/g</span>
                  </div>
                </div>
                <v-divider vertical />
                <div class="text-center ml-2 mr-2">
                  <div class="text-caption text-grey" style="font-size:10px;">평균가</div>
                  <div class="text-body-2 font-weight-bold" style="color:#e5e7eb;">
                    {{ formatPrice(silverStats.avg) }}<span class="text-caption text-grey ml-1">원/g</span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4">
          <!-- 데이터 없음 -->
          <div
            v-if="!hasSilver"
            class="d-flex flex-column justify-center align-center text-grey py-10"
          >
            <v-icon size="48" color="grey-lighten-2" class="mb-2">mdi-chart-line-variant</v-icon>
            <p class="text-body-2">조회된 은 시세 데이터가 없습니다.</p>
          </div>

          <!-- 은 차트 컨테이너 -->
          <div
            v-else
            ref="silverChartContainer"
            style="width:100%; height:300px;"
          />
        </v-card-text>
      </v-card>

    </template>
  </div>
</template>

<style scoped>
.metal-price-page :deep(.v-card) {
  background: rgba(29, 33, 44, 0.94) !important;
}
</style>
